import dayjs from 'dayjs';

/** Earliest date a chart's custom range may start on. */
export const CUSTOM_RANGE_MIN_DATE = '2023-01-01';
/** Longest custom range (end - start, in days). Future dates are allowed, the span is still capped. */
export const CUSTOM_RANGE_MAX_DAYS = 90;

export interface CustomDateRangeCheck {
  valid: boolean;
  error: string;
  /** Some part of the range is after today: charts show the data available so far. */
  includesFuture: boolean;
  /** The whole range is after today: there is no data yet. */
  entirelyFuture: boolean;
}

/** Last end date allowed for a given start date (start + 90 days), as YYYY-MM-DD; '' when start is not a valid date. */
export function maxEndDateFor(start: string | null | undefined): string {
  const s = dayjs(start);
  return start && s.isValid() ? s.add(CUSTOM_RANGE_MAX_DAYS, 'day').format('YYYY-MM-DD') : '';
}

/** Validates a custom range. Future dates are valid; the 90-day span and the 2023 floor still apply. */
export function checkCustomDateRange(
  startDate: string | null | undefined,
  endDate: string | null | undefined,
  today: dayjs.Dayjs = dayjs()
): CustomDateRangeCheck {
  const result: CustomDateRangeCheck = { valid: false, error: '', includesFuture: false, entirelyFuture: false };
  if (!startDate || !endDate) {
    result.error = 'Please select a valid date range.';
    return result;
  }
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  if (!start.isValid() || !end.isValid()) {
    result.error = 'Please select valid dates.';
    return result;
  }
  result.includesFuture = end.isAfter(today, 'day');
  result.entirelyFuture = start.isAfter(today, 'day');

  if (start.isBefore(dayjs(CUSTOM_RANGE_MIN_DATE), 'day')) {
    result.error = 'Start date cannot be before January 1, 2023.';
  } else if (end.isBefore(start, 'day')) {
    result.error = 'End date must be after or equal to start date.';
  } else if (end.diff(start, 'day') > CUSTOM_RANGE_MAX_DAYS) {
    result.error = `Custom range cannot exceed ${CUSTOM_RANGE_MAX_DAYS} days.`;
  } else {
    result.valid = true;
  }
  return result;
}
